import { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Clock,
  CalendarDays,
  DollarSign,
  Building2,
  Timer,
  Settings,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
  Palmtree,
  CalendarCheck,
  ClockAlert,
  FileText,
  ListChecks,
  ReceiptText,
  BookUser,
  Globe,
  IndianRupee,
  BarChart2,
  Megaphone,
  Inbox,
  Receipt,
} from 'lucide-react';
import type { UserRole } from '@vayu-saathi/shared-types';
import { useAuthStore } from '@/stores/auth.store';
import { useUiStore } from '@/stores/ui.store';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { BrandMark } from '@/components/brand/BrandMark';
import { APP_NAME } from '@/lib/brand';
import { useApprovalInboxCount } from '@/hooks/useApprovalInbox';
import { useExpenseSummary } from '@/hooks/useExpenses';
import { isExpenseReviewer, isReviewerSummary } from '@/lib/expenses';

type IconType = React.ComponentType<{ className?: string }>;

interface NavItem {
  label: string;
  to: string;
  icon: IconType;
  allow?: UserRole[];
  /** Shows a live count pill (pending approvals / claims needing the user's action). */
  badge?: 'approvals' | 'claims';
}

interface NavGroup {
  key: string;
  label: string;
  icon: IconType;
  to?: string; // optional: makes the group header itself a navigable link
  children: NavItem[];
}

type SidebarEntry = ({ group: false } & NavItem) | ({ group: true } & NavGroup);

const ENTRIES: SidebarEntry[] = [
  { group: false, label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard },
  {
    group: false,
    label: 'Employees',
    to: '/employees',
    icon: Users,
    allow: ['SUPER_ADMIN', 'ORG_ADMIN', 'HR', 'MANAGER'],
  },
  { group: false, label: 'Attendance', to: '/attendance', icon: Clock },
  { group: false, label: 'Reimbursements', to: '/reimbursements', icon: Receipt, badge: 'claims' },
  {
    group: true,
    key: 'leaves',
    label: 'Leaves',
    icon: CalendarDays,
    children: [
      {
        label: 'Leave Management',
        to: '/leaves',
        icon: ListChecks,
        allow: ['SUPER_ADMIN', 'ORG_ADMIN', 'HR', 'MANAGER'],
      },
      { label: 'Holiday Calendar', to: '/holidays', icon: Palmtree },
      { label: 'My Leaves', to: '/my-leaves', icon: CalendarCheck },
      { label: 'Regularisation', to: '/regularisation', icon: ClockAlert },
    ],
  },
  {
    group: true,
    key: 'payroll',
    label: 'Payroll',
    icon: DollarSign,
    children: [
      {
        label: 'Payroll Runs',
        to: '/payroll',
        icon: ReceiptText,
        allow: ['SUPER_ADMIN', 'ORG_ADMIN', 'HR'],
      },
      {
        label: 'Salary Structure',
        to: '/salary-structure',
        icon: BarChart2,
        allow: ['SUPER_ADMIN', 'ORG_ADMIN', 'HR'],
      },
      { label: 'My Payslips', to: '/my-payslips', icon: IndianRupee },
      { label: 'My Letters', to: '/my-letters', icon: FileText },
    ],
  },
  {
    group: true,
    key: 'company',
    label: 'Company',
    icon: Globe,
    children: [
      { label: 'Announcements', to: '/announcements', icon: Megaphone },
      { label: 'Employee Directory', to: '/directory', icon: BookUser },
      // Hidden for the field launch: { label: 'Organisation Chart', to: '/org-chart', icon: Network },
    ],
  },
  {
    group: false,
    label: 'Reports',
    to: '/reports',
    icon: FileText,
    allow: ['SUPER_ADMIN', 'ORG_ADMIN', 'HR'],
  },
  {
    group: false,
    label: 'Departments',
    to: '/departments',
    icon: Building2,
    allow: ['SUPER_ADMIN', 'ORG_ADMIN', 'HR'],
  },
  {
    group: false,
    label: 'Shifts',
    to: '/shifts',
    icon: Timer,
    allow: ['SUPER_ADMIN', 'ORG_ADMIN', 'HR'],
  },
  {
    group: false,
    label: 'Approval Inbox',
    to: '/approval-inbox',
    icon: Inbox,
    allow: ['SUPER_ADMIN', 'ORG_ADMIN', 'HR', 'MANAGER'],
    badge: 'approvals',
  },
  // Hidden for the field launch (pages kept, routes redirect to /dashboard):
  //   Office Locations (/office-locations), HR Policies (/hr-policies), Organisation Chart (/org-chart)
  { group: false, label: 'Settings', to: '/settings', icon: Settings },
];

function CountPill({ count, className }: { count: number; className?: string }) {
  if (count <= 0) return null;
  return (
    <span
      className={cn(
        'bg-primary text-primary-foreground inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-bold tabular-nums leading-none',
        className,
      )}
      aria-label={`${String(count)} pending`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

function isVisible(item: NavItem, role: UserRole | undefined): boolean {
  return !item.allow || (role != null && item.allow.includes(role));
}

// ── Expanded mode item classes ──
function expandedItemClass(isActive: boolean, indent: boolean = false) {
  return cn(
    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
    indent && 'ml-4',
    isActive
      ? 'bg-sidebar-primary text-sidebar-primary-foreground font-semibold shadow-[inset_3px_0_0_var(--brand-orange)]'
      : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
  );
}

// ── Collapsed mode: fixed 36×36 square, mx-auto in li ──
function collapsedIconClass(isActive: boolean) {
  return cn(
    'relative flex h-9 w-9 items-center justify-center rounded-md transition-colors',
    isActive
      ? 'bg-sidebar-primary text-sidebar-primary-foreground font-semibold shadow-[inset_3px_0_0_var(--brand-orange)]'
      : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
  );
}

function flyoutItemClass(isActive: boolean) {
  return cn(
    'flex w-full items-center gap-2.5 rounded-sm px-3 py-2 text-sm transition-colors',
    isActive
      ? 'bg-accent font-semibold text-accent-foreground'
      : 'text-popover-foreground hover:bg-accent hover:text-accent-foreground',
  );
}

export function Sidebar() {
  const role = useAuthStore((s) => s.user?.role);
  const orgName = useAuthStore((s) => s.user?.orgName);
  const orgLogoUrl = useAuthStore((s) => s.user?.orgLogoUrl);
  const { sidebarOpen, toggleSidebar, setSidebarOpen } = useUiStore();
  const location = useLocation();
  const navigate = useNavigate();

  const isApprover = role != null && ['SUPER_ADMIN', 'ORG_ADMIN', 'HR', 'MANAGER'].includes(role);
  const { data: inboxCount = 0 } = useApprovalInboxCount({ enabled: isApprover });
  const { data: claimSummary } = useExpenseSummary({ enabled: isExpenseReviewer(role) });
  const badgeCounts: Record<NonNullable<NavItem['badge']>, number> = {
    approvals: inboxCount,
    claims: isReviewerSummary(claimSummary) ? claimSummary.actionRequired : 0,
  };

  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [flyoutGroup, setFlyoutGroup] = useState<string | null>(null);
  const [sidebarVisible, setSidebarVisible] = useState(sidebarOpen);

  useEffect(() => {
    if (window.innerWidth < 768) setSidebarOpen(false);
    setFlyoutGroup(null);
  }, [location.pathname, setSidebarOpen]);

  useEffect(() => {
    if (sidebarOpen) setFlyoutGroup(null);
  }, [sidebarOpen]);

  useEffect(() => {
    if (sidebarOpen) {
      setSidebarVisible(true);
      return;
    }
    setOpenGroups(new Set());
    const t = setTimeout(() => {
      setSidebarVisible(false);
    }, 200);
    return () => {
      clearTimeout(t);
    };
  }, [sidebarOpen]);

  // Auto-open the group whose child matches the current path; close all when no group matches
  useEffect(() => {
    if (!sidebarOpen) return;
    for (const entry of ENTRIES) {
      if (!entry.group) continue;
      const hasActive = entry.children.some(
        (c) => location.pathname === c.to || location.pathname.startsWith(c.to + '/'),
      );
      // Also open when on the group's own page (for groups that have a `to`)
      const parentActive =
        entry.to &&
        (location.pathname === entry.to || location.pathname.startsWith(entry.to + '/'));
      if (hasActive || parentActive) {
        setOpenGroups(new Set([entry.key]));
        return;
      }
    }
    // Navigated to a flat route — close all accordions so the flat item can show active
    setOpenGroups(new Set());
  }, [location.pathname, sidebarOpen]);

  function handleGroupClick(key: string, visible: NavItem[]) {
    const isCurrentlyOpen = openGroups.has(key);
    setOpenGroups(isCurrentlyOpen ? new Set() : new Set([key]));
    if (!isCurrentlyOpen) {
      const firstChild = visible[0];
      if (firstChild) void navigate(firstChild.to);
    }
  }

  // True when an open accordion's children don't include the current URL.
  // Used to suppress the flat-item active highlight in that case.
  const hasOpenGroupWithoutActiveChild = [...openGroups].some((key) => {
    const group = ENTRIES.find((e) => e.group && (e as NavGroup).key === key) as
      | ({ group: true } & NavGroup)
      | undefined;
    if (!group) return false;
    const visibleChildren = group.children.filter((c) => isVisible(c, role));
    return !visibleChildren.some(
      (c) => location.pathname === c.to || location.pathname.startsWith(c.to + '/'),
    );
  });

  return (
    <TooltipProvider delayDuration={400}>
      <>
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/50 md:hidden"
            onClick={() => {
              setSidebarOpen(false);
            }}
            aria-hidden="true"
          />
        )}

        <aside
          id="tour-sidebar"
          className={cn(
            'bg-sidebar fixed left-0 top-0 z-40 flex h-full flex-col overflow-hidden border-r transition-[width,transform] duration-200',
            sidebarOpen ? 'w-64' : 'w-16',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
          )}
        >
          {/* ── Header: logo + brand + collapse toggle ── */}
          <div
            className={cn(
              'flex h-16 shrink-0 items-center border-b',
              sidebarVisible ? 'px-4' : 'justify-center',
            )}
          >
            {sidebarVisible ? (
              <>
                <div className="flex flex-1 items-center gap-2.5 overflow-hidden">
                  {orgLogoUrl ? (
                    <img
                      src={orgLogoUrl}
                      alt={orgName ?? 'Logo'}
                      className="h-8 w-8 shrink-0 rounded-lg object-contain"
                    />
                  ) : (
                    <BrandMark />
                  )}
                  <span className="font-display text-sidebar-foreground truncate text-base font-bold">
                    {orgName ?? APP_NAME}
                  </span>
                </div>
                {/* Desktop-only collapse button in header */}
                <button
                  onClick={toggleSidebar}
                  className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ml-1 hidden h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors md:flex"
                  aria-label="Collapse sidebar"
                >
                  <PanelLeftClose className="h-4 w-4" />
                </button>
              </>
            ) : (
              /* In collapsed state the expand button IS the header */
              <button
                onClick={toggleSidebar}
                className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hidden h-9 w-9 items-center justify-center rounded-md transition-colors md:flex"
                aria-label="Expand sidebar"
              >
                <PanelLeftOpen className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* ── Navigation ── */}
          <nav
            className={cn(
              'flex-1 overflow-y-auto py-3',
              sidebarVisible ? '[scrollbar-gutter:stable]' : 'overflow-x-hidden',
            )}
          >
            {/* Expanded: px-2 gutters. Collapsed: fixed-height slots, centered */}
            <ul className={cn(sidebarVisible ? 'space-y-0.5 px-2' : 'space-y-px')}>
              {ENTRIES.map((entry) => {
                // ── Group entries ──
                if (entry.group) {
                  const visible = entry.children.filter((c) => isVisible(c, role));
                  if (visible.length === 0) return null;

                  // Collapsed → Popover flyout
                  if (!sidebarVisible) {
                    const isChildActive = visible.some(
                      (c) => location.pathname === c.to || location.pathname.startsWith(c.to + '/'),
                    );
                    return (
                      <li key={entry.key} className="flex h-10 items-center justify-center">
                        <Popover
                          open={flyoutGroup === entry.key}
                          onOpenChange={(open) => {
                            setFlyoutGroup(open ? entry.key : null);
                          }}
                        >
                          <PopoverTrigger asChild>
                            <button
                              className={collapsedIconClass(flyoutGroup === entry.key)}
                              aria-label={entry.label}
                            >
                              <entry.icon className="h-5 w-5 shrink-0" />
                              {isChildActive && flyoutGroup !== entry.key && (
                                <span className="bg-primary/50 absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full blur-[2px]" />
                              )}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            side="right"
                            align="start"
                            sideOffset={10}
                            className="w-52 p-0"
                          >
                            <div className="border-b px-3 py-2.5">
                              <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                                {entry.label}
                              </p>
                            </div>
                            <div className="py-1">
                              {/* Parent overview link for groups that have their own page */}
                              {entry.to && (
                                <>
                                  <NavLink
                                    to={entry.to}
                                    end
                                    className={({ isActive }) => flyoutItemClass(isActive)}
                                    onClick={() => {
                                      setFlyoutGroup(null);
                                    }}
                                  >
                                    <entry.icon className="h-4 w-4 shrink-0" />
                                    <span>Overview</span>
                                  </NavLink>
                                  {visible.length > 0 && <div className="my-1 border-t" />}
                                </>
                              )}
                              {visible.map((child) => {
                                return (
                                  <NavLink
                                    key={child.to}
                                    to={child.to}
                                    className={({ isActive }) => flyoutItemClass(isActive)}
                                    onClick={() => {
                                      setFlyoutGroup(null);
                                    }}
                                  >
                                    <child.icon className="h-4 w-4 shrink-0" />
                                    <span>{child.label}</span>
                                  </NavLink>
                                );
                              })}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </li>
                    );
                  }

                  // Expanded → inline accordion
                  const isOpen = openGroups.has(entry.key);
                  const isChildActive = visible.some(
                    (c) => location.pathname === c.to || location.pathname.startsWith(c.to + '/'),
                  );

                  // ── Groups with their own navigable page (e.g. Performance) ──
                  // Use a plain <button> — identical DOM structure to standard groups.
                  // No nesting, no wrapper: the ChevronDown is a direct flex child at px-3 right,
                  // exactly matching Leaves/Payroll/Company/People in every pixel.
                  // Click when NOT on the page → navigate (useEffect auto-opens the accordion).
                  // Click when already on the page → toggle accordion only.
                  if (entry.to) {
                    const parentTo = entry.to;
                    const isParentActive =
                      location.pathname === parentTo ||
                      location.pathname.startsWith(parentTo + '/');
                    return (
                      <li key={entry.key}>
                        <button
                          onClick={() => {
                            if (!isParentActive) {
                              void navigate(parentTo);
                              // useEffect will auto-open the group once location changes
                            } else {
                              // Already on this page — just toggle the accordion
                              setOpenGroups(isOpen ? new Set() : new Set([entry.key]));
                            }
                          }}
                          className={cn(
                            'group/nav flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                            isParentActive
                              ? 'bg-sidebar-primary text-sidebar-primary-foreground font-semibold shadow-[inset_3px_0_0_var(--brand-orange)]'
                              : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                          )}
                        >
                          <entry.icon className="h-5 w-5 shrink-0" />
                          <span className="flex-1 text-left">{entry.label}</span>
                          {isChildActive && !isOpen && (
                            <span className="bg-primary/50 h-2 w-2 shrink-0 rounded-full blur-[2px]" />
                          )}
                          <ChevronDown
                            className={cn(
                              'h-4 w-4 shrink-0 transition-all duration-200',
                              isOpen
                                ? 'rotate-180 opacity-100'
                                : 'opacity-40 group-hover/nav:opacity-100',
                            )}
                          />
                        </button>
                        <div
                          className={cn(
                            'grid overflow-hidden transition-[grid-template-rows] duration-200',
                            isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                          )}
                        >
                          <ul className="mt-0.5 min-h-0 space-y-0.5">
                            {visible.map((child) => {
                              return (
                                <li key={child.to}>
                                  <NavLink
                                    to={child.to}
                                    className={({ isActive }) => expandedItemClass(isActive, true)}
                                  >
                                    <child.icon className="h-4 w-4 shrink-0" />
                                    <span>{child.label}</span>
                                  </NavLink>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </li>
                    );
                  }

                  // ── Standard groups (header-only toggle, navigates to first child on open) ──
                  return (
                    <li key={entry.key}>
                      <button
                        onClick={() => {
                          handleGroupClick(entry.key, visible);
                        }}
                        className="group/nav text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors"
                      >
                        <entry.icon className="h-5 w-5 shrink-0" />
                        <span className="flex-1 text-left">{entry.label}</span>
                        {isChildActive && !isOpen && (
                          <span className="bg-primary/50 h-2 w-2 shrink-0 rounded-full blur-[2px]" />
                        )}
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 shrink-0 transition-all duration-200',
                            isOpen
                              ? 'rotate-180 opacity-100'
                              : 'opacity-40 group-hover/nav:opacity-100',
                          )}
                        />
                      </button>
                      <div
                        className={cn(
                          'grid overflow-hidden transition-[grid-template-rows] duration-200',
                          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                        )}
                      >
                        <ul className="mt-0.5 min-h-0 space-y-0.5">
                          {visible.map((child) => {
                            return (
                              <li key={child.to}>
                                <NavLink
                                  to={child.to}
                                  className={({ isActive }) => expandedItemClass(isActive, true)}
                                >
                                  <child.icon className="h-4 w-4 shrink-0" />
                                  <span>{child.label}</span>
                                </NavLink>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </li>
                  );
                }

                // ── Flat entries ──
                if (!isVisible(entry, role)) return null;

                // Collapsed → centered square icon + tooltip
                if (!sidebarVisible) {
                  return (
                    <li key={entry.to} className="flex h-10 items-center justify-center">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <NavLink
                            to={entry.to}
                            className={({ isActive }) =>
                              collapsedIconClass(isActive && !hasOpenGroupWithoutActiveChild)
                            }
                            aria-label={entry.label}
                          >
                            <entry.icon className="h-5 w-5 shrink-0" />
                            {entry.badge && badgeCounts[entry.badge] > 0 && (
                              <span className="bg-primary ring-sidebar absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full ring-2" />
                            )}
                          </NavLink>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          {entry.label}
                          {entry.badge && badgeCounts[entry.badge] > 0
                            ? ` (${String(badgeCounts[entry.badge])})`
                            : ''}
                        </TooltipContent>
                      </Tooltip>
                    </li>
                  );
                }

                // Expanded
                return (
                  <li key={entry.to}>
                    <NavLink
                      to={entry.to}
                      className={({ isActive }) =>
                        expandedItemClass(isActive && !hasOpenGroupWithoutActiveChild)
                      }
                    >
                      <entry.icon className="h-5 w-5 shrink-0" />
                      <span className="flex-1">{entry.label}</span>
                      {entry.badge && <CountPill count={badgeCounts[entry.badge]} />}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>
      </>
    </TooltipProvider>
  );
}
